'use client';

import { useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { $paywallStore } from 'libs/features/paywall/src/lib/store';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { USER_VARIABLE } from '@nx-frontend/shared/api';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $orgCoupons, $hasCoupons, $isCouponLoaded } from '@nx-frontend/shared/store/org-coupons';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $showActivateTrial, $showPastDueTrial, $businessTrial } from '@nx-frontend/shared/store/trial';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { $userVars } from '@nx-frontend/shared/store/user-vars';
import {
  BillingInfoPlan,
  OrgInfoSubscriptionType,
  PaywallPopupPayload,
  PaywallPopupReason,
  PopupType,
} from '@nx-frontend/shared/types';
import { UnleashFeatures } from '@nx-frontend/shared/types';
import { getSubscriptionType } from '@nx-frontend/shared/utils';
import { getOpenPaywallPopup, getOpenFromCallEndPopup } from '@nx-frontend/widgets/popups';

export function TrialWrapper() {
  const [
    showActivateTrial,
    showPastDueTrial,
    isCouponLoaded,
    hasCoupons,
    billingInfo,
    orgInfo,
    orgCoupons,
    openModal,
    userVars,
    currentOrganization,
    businessTrial,
    unleashFlags,
  ] = useUnit([
    $showActivateTrial,
    $showPastDueTrial,
    $isCouponLoaded,
    $hasCoupons,
    $billingInfo,
    $orgInfo,
    $orgCoupons,
    openModalEvent,
    $userVars,
    $currentOrganization,
    $businessTrial,
    $unleashFlags,
  ]);

  const [paywallState] = useUnit([$paywallStore]);
  const t = useTranslations('features');

  const isCardTrial = useMemo(() => {
    return (
      (typeof window === 'object' && window.location.search.match(/trial_activated=1$/)) ||
      (paywallState.orgBillingInfo?.trialEndsAt ?? 0) > Date.now() / 1000
    );
  }, [paywallState.orgBillingInfo]);

  useEffect(() => {
    if (window.location.search.match(/^\?trial_activated=1$/) && !window.location.search.match(/^\?after_purchase=1/)) {
      setTimeout(() => {
        openModal({
          content: PopupType.TRIAL_WELCOME,
          showCloseButton: false,
          payload: {
            payloadType: PopupType.TRIAL_WELCOME,
            animated: true, // Welcome popup with animation
          },
        });
      }, 250);
    }
    if (window.location.search.match(/^\?after_purchase=1/)) {
      setTimeout(() => {
        getOpenPaywallPopup({
          openModal,
          payload: {
            payloadType: PopupType.PAYWALL,
            mode: 'addons',
            title: isCardTrial ? t('paywall.title_trial_started') : t('paywall.title_subscription_activated'),
            reason: PaywallPopupReason.TRIAL_WELCOME,
            orgId: currentOrganization.globalId,
          },
        });
      }, 250);
    }
    if (window.location.search.match(/^\?trial_wow_popup=1$/)) {
      openModal({
        content: PopupType.TRIAL_WELCOME,
        showCloseButton: false,
        payload: {
          payloadType: PopupType.TRIAL_WELCOME,
          animated: false, // Wow, you're lucky today!
        },
      });
    }
  }, [openModal, currentOrganization, isCardTrial, t]);

  useEffect(() => {
    if (showPastDueTrial) {
      const fromCallReg = '1' === userVars.find((uv) => uv.key === USER_VARIABLE.FROM_CALL)?.value ? true : false;
      if (
        fromCallReg &&
        unleashFlags[UnleashFeatures.FROM_CALL_PASTDUE] &&
        businessTrial &&
        'pastdue' === businessTrial.state
      ) {
        getOpenFromCallEndPopup({
          openModal,
          payload: {
            payloadType: PopupType.FROM_CALL_END,
            orgId: currentOrganization.globalId,
          },
        });
      } else {
        const popupPayload: PaywallPopupPayload = {
          payloadType: PopupType.PAYWALL,
          mode: 'trialEnd',
          title: `${t('paywall.title_trial_has_ended')}\n${t('paywall.want_to_keep_features')}`,
          subscriptionType: OrgInfoSubscriptionType.STANDARD,
          escapeToClose: false,
        };

        getOpenPaywallPopup({ openModal, payload: popupPayload });
      }
    }
  }, [openModal, showPastDueTrial, userVars, currentOrganization.globalId, businessTrial, unleashFlags, t]);

  useEffect(() => {
    if (!isCouponLoaded) {
      return;
    }

    if (hasCoupons) {
      return;
    }

    const subscriptionType = getSubscriptionType({ billingInfo, orgInfo, orgCoupons });

    let showTrialStartPopup = showActivateTrial;

    if (subscriptionType === OrgInfoSubscriptionType.STANDARD) {
      if (billingInfo.plan !== BillingInfoPlan.NOPE) {
        showTrialStartPopup = false;
      }
    } else {
      showTrialStartPopup = false;
    }

    if (showTrialStartPopup) {
      const popupPayload: PaywallPopupPayload = {
        payloadType: PopupType.PAYWALL,
        mode: 'trialStart',
        title: t('paywall.title_organization_ready'),
        escapeToClose: false,
      };

      getOpenPaywallPopup({ openModal, payload: popupPayload });
    }
  }, [openModal, showActivateTrial, isCouponLoaded, hasCoupons, billingInfo, orgInfo, orgCoupons, t]);

  return null;
}
